// Angular Files
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

// Other External Files
import { firstValueFrom } from 'rxjs';

// Payment Integration Files
// For some reason if the PaymentProcessorService was included from just base, the unit tests would fail
import { PaymentProcessorService } from 'apps/public-portal/src/app/payment-integrations/base/interfaces';

// Teller Online Files
import { RedirectPaymentStatusEnum, RedirectVerifyPaymentStatusRequestDto, RedirectVerifyPaymentStatusResponseDto, SharedPaymentIntegrationApiClient } from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';
import { RedirectPaymentProcessorResponse } from 'apps/public-portal/src/app/payment-integrations/base/models';
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import { TellerOnlineWindowService } from 'teller-online-libraries/shared';
import { CartService } from 'apps/public-portal/src/app/core/services';

@Injectable({
    providedIn: 'root'
})
export class LexisNexisService extends PaymentProcessorService {
    // Config values populated via assignAdditionalProps from PopulateAdditionalData
    private baseUrl: string;
    private lexisNexisCode: string;
    private productCode: string;

    //#region PaymentProcessorService fields

    public static override redirect = true;
    public systemPaymentIdentifierKey = "ref";

    //#endregion

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private windowService: TellerOnlineWindowService,
        private appService: TellerOnlineAppService,
        private cartService: CartService,
        public sharedPaymentApi: SharedPaymentIntegrationApiClient,
    ) {
        super(sharedPaymentApi);
    }

    //#region PaymentProcessorService

    public override async checkout(cartGuid: string) {
        try {
            const response = await firstValueFrom(this.sharedPaymentApi.startPayment(cartGuid));

            // Build the ORDER_XML in the frontend following the LN form POST spec
            const orderXml = this.buildOrderXml(response.paymentIdentifier);
            this.submitFormPost(response.redirectUrl, orderXml);
        } catch(e) {
            this._errorEvent.next('Failed to start payment');
        }
    }

    public override async handlePostbackResponse(params) {
        const verifyPaymentStatusRequest = new RedirectVerifyPaymentStatusRequestDto();
        const paymentIdentifier = params[this.systemPaymentIdentifierKey];
        verifyPaymentStatusRequest.paymentIdentifier = paymentIdentifier;
        // LN only redirects back via bypassReceiptUrl after a completed payment.
        // There is no cancel/back button on the LN hosted payment page — if the user
        // abandons, they simply never return and the payment remains in Processing.
        verifyPaymentStatusRequest.paymentStatus = RedirectPaymentStatusEnum.Success;

        let response: RedirectVerifyPaymentStatusResponseDto;
        if (paymentIdentifier) {
            response = await firstValueFrom(
                this.sharedPaymentApi.verifyPaymentStatus(verifyPaymentStatusRequest)
            );
        }

        let state: {cartError?: string, emailAddress?: string} = {};
        let redirectUrl = "/";
        let success = response?.responseSuccess;

        if (success) {
            redirectUrl = "/checkout/success/" + this.cartService.cartGuid;
        } else {
            state.cartError = response.responseMessage;
        }

        this.appService.consoleLog(this, state);

        return new RedirectPaymentProcessorResponse({
            paymentIdentifier: paymentIdentifier,
            cartGuid: this.cartService.cartGuid,
            status: success ? "success" : "cancel",
            redirectUrl,
            navigationExtras: { state }
        });
    }

    //#endregion

    /**
     * Builds the LexisNexis ORDER_XML for the form POST.
     */
    private buildOrderXml(paymentIdentifier: string): string {
        const bypassReceiptUrl = `${this.windowService.location.origin}/checkout/processing?${this.systemPaymentIdentifierKey}=${paymentIdentifier}`;
        const productAmount = this.cartService.cartSnapshot.items
            .reduce((acc, item) => acc + (item.amountInCart * (item.quantity ?? 1)), 0)
            .toFixed(2);

        return `<?xml version="1.0" encoding="utf-8"?>
<multiLineFormPostRequest>
    <lexisNexisCode>${this.lexisNexisCode}</lexisNexisCode>
    <clientOrderId>${paymentIdentifier}</clientOrderId>
    <bypassReceiptUrl>${bypassReceiptUrl}</bypassReceiptUrl>
    <paymentLineItems>
        <productLines>
            <productAmount>${productAmount}</productAmount>
            <lexisNexisProductCode>${this.productCode}</lexisNexisProductCode>
            <refField>${paymentIdentifier}</refField>
            <refField1/>
            <refField2/>
            <refField3/>
            <refField4/>
            <refField5/>
        </productLines>
    </paymentLineItems>
</multiLineFormPostRequest>`;
    }

    /**
     * Submits a form POST to LexisNexis with the ORDER_XML data.
     */
    private submitFormPost(url: string, orderXml: string): void {
        const form = this.document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.style.display = 'none';

        const input = this.document.createElement('input');
        input.type = 'hidden';
        input.name = 'ORDER_XML';
        input.value = orderXml;
        form.appendChild(input);

        this.document.body.appendChild(form);
        form.submit();
    }
}
